import { useLayoutEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Locale } from '@ivario-arc/shared/src/types/locales';
import { Paths } from '@src/routes/paths';
import { updateKeycloakLocale } from '@src/api/keycloak';
import {
  consumePendingLanguageSync,
  setStoredLanguage,
  toSupportedLocale,
} from '@utils/language';

export function CallbackView() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation('language');
  const initRef = useRef(false);

  // Use useLayoutEffect to avoid flickering in case the auth state is already set
  useLayoutEffect(() => {
    if (auth.isLoading || initRef.current) return;
    initRef.current = true;

    const pendingLang = consumePendingLanguageSync();
    const profileLang = toSupportedLocale(auth.user?.profile?.locale);
    const accessToken = auth.user?.access_token;

    // Resolve a language only when we actually have a signal. CallbackView also
    // mounts inside the silent-renew iframe; on failure (no SSO session) we
    // must NOT write any fallback to ivario_lang, otherwise the landing-page
    // overlay would never reappear for users who never chose a language.
    let effective: Locale | null = null;
    if (pendingLang) {
      // User explicitly chose a language on the landing page; that wins and is
      // pushed to Keycloak so the account locale stays in sync.
      effective = pendingLang;
      if (accessToken && pendingLang !== profileLang) {
        updateKeycloakLocale(accessToken, pendingLang).catch(() => {
          // Failed to persist language to Keycloak — not blocking
        });
      }
    } else if (profileLang) {
      // No fresh landing-page choice: trust Keycloak's locale, which may have
      // been changed via the Keycloak account/settings page.
      effective = profileLang;
    }

    if (effective) {
      void i18n.changeLanguage(effective);
      setStoredLanguage(effective);
    }

    const returnTo = (auth.user?.state as { returnTo: string } | undefined)
      ?.returnTo;
    navigate(returnTo ?? Paths.PROFILE, { replace: true });
  }, [
    auth.isLoading,
    auth.user?.access_token,
    auth.user?.profile?.locale,
    auth.user?.state,
    i18n,
    navigate,
  ]);

  if (auth.error) {
    // "No matching state found in storage" happens when the callback URL is
    // hit without a valid OIDC state (e.g. cross-tab login, back-button,
    // bots). Redirect to login instead of crashing the app.
    if (auth.error.message.includes('No matching state found in storage')) {
      navigate(Paths.LOGIN, { replace: true });
      return <div>Loading...</div>;
    }
    throw auth.error;
  }

  return <div>Loading...</div>;
}
